import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const GenerateScheduleLoader = ({
  open,
  onClose,
  isLoading,
  estimatedTimeInMs = 15000,
}: {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  estimatedTimeInMs?: number;
}) => {
  const { t } = useTranslation();
  const STATIC_STEPS = useMemo(() => [
    t("full-time-shifts_step-collecting-demand"),
    t("full-time-shifts_step-finding-workers"),
    t("full-time-shifts_step-identifying-gaps"),
    t("full-time-shifts_step-moving-workers"),
  ], [t]);
  const steps = useMemo(() => [...STATIC_STEPS, t("full-time-shifts_step-done")], [STATIC_STEPS, t]);
  const [activeStep, setActiveStep] = useState(0);
  const [dots, setDots] = useState("");
  const STEP_DURATION = estimatedTimeInMs / STATIC_STEPS.length;

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setDots("");
      return;
    }

    setActiveStep(0);
    setDots("");

    let stepIndex = 0;

    const interval = setInterval(() => {
      if (stepIndex < STATIC_STEPS.length - 1) {
        stepIndex++;
        setActiveStep(stepIndex);
      } else {
        clearInterval(interval); // pause at "Moving Workers"
      }
    }, STEP_DURATION);

    return () => clearInterval(interval);
  }, [open, STEP_DURATION, STATIC_STEPS.length]);

  useEffect(() => {
    if (!open) return;
    if (!isLoading) {
      setActiveStep(steps.length - 1); // Jump to "Done!"
      setDots(""); // no dots on Done
    }
  }, [isLoading, open, steps.length]);

  useEffect(() => {
    if (!open || activeStep === steps.length - 1) {
      setDots("");
      return;
    }

    const dotInterval = setInterval(() => {
      setDots((prevDots) => {
        if (prevDots.length < 3) {
          return prevDots + ".";
        }
        return "";
      });
    }, 300);

    return () => clearInterval(dotInterval);
  }, [open, activeStep, steps.length]);

  const progressPercent = ((activeStep + 1) / steps.length) * 100;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Box
        sx={{
          position: "relative",
          height: "16px",
          width: "171px",
          borderRadius: "8px",
          overflow: "hidden",
          background: "linear-gradient(90deg, #4196E3 0%, #373598 97.66%)",
          padding: "1px",
        }}
      >
        {/* Filling portion of the progress bar with slashes */}
        <Box
          sx={{
            position: "absolute",
            top: 1,
            left: 1,
            height: "14px",
            borderRadius: "7px",
            width: `${progressPercent}%`,
            background: `
                        repeating-linear-gradient(
                          -45deg, /* Angle of the slashes */
                          #E0E8F5 0, /* Start of light slash */
                          #C8D6ED 9px, /* slashPerpendicularWidth */
                          transparent 3px, /* gapPerpendicularWidth */
                          transparent 12px /* totalPatternPerpendicularWidth */
                        )
                      `,
            backgroundSize: "16.97px 16.97px", //patternSide = sqrt(2) * totalPatternPerpendicularWidth => approx 1.414 * 12 = 16.97px
            backgroundRepeat: "repeat-x",
            backgroundPosition: "0 0",
            transition: "width 0.4s ease",
          }}
        />
      </Box>

      <Box
        sx={{
          height: "30px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: "10px",
        }}
      >
        <Typography
          key={activeStep}
          variant="caption"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            color: "rgba(0,0,0,1)",
            animation: "slideInFromBottom 0.5s ease-out forwards",
            fontFamily: "Roboto",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "12px",
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
          }}
        >
          {/* Main text label */}
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "Roboto",
              color: "rgba(0,0,0,0.87)",
            }}
          >
            {steps[activeStep]}
          </Typography>
          {/* Ellipsis dots in a separate span with fixed width */}
          {activeStep < steps.length - 1 && (
            <Typography
              sx={{
                minWidth: "1em",
                display: "inline-block",
                textAlign: "left",
                whiteSpace: "pre",
                fontSize: "12px",
              }}
            >
              {dots}
            </Typography>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default GenerateScheduleLoader;
